import React, { useCallback, useMemo, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { Button, Input, Card, Loader, ConfirmacionDialog } from '@rfacil/shared/ui';
import { useForm, useNotificacionStore } from '@rfacil/shared/hooks';
import { validationUtils } from '@rfacil/shared/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useRestablecerContraseniaRecuperacion } from '../hooks/useEnvioCorreo';

/** Estado esperado al navegar desde el flujo de olvidé contraseña (código validado). */
export type RestablecerPasswordState = {
  correo: string;
  claveRecuperacion: string;
};

type FormValues = {
  nuevaContrasena: string;
  confirmarContrasena: string;
};

function isValidState(s: unknown): s is RestablecerPasswordState {
  if (!s || typeof s !== 'object') return false;
  const o = s as Record<string, unknown>;
  return (
    typeof o.correo === 'string' &&
    o.correo.trim().length > 0 &&
    typeof o.claveRecuperacion === 'string' &&
    o.claveRecuperacion.length > 0
  );
}

/**
 * Pantalla de restablecimiento tras validar el código por correo: solo nueva contraseña y confirmación.
 * Usa `POST /Login/RecuperarContrasenia`; el código validado va en `PSSActual`.
 */
export const RestablecerPassword: React.FC = () => {
  const location = useLocation();
  const { showNotification } = useNotificacionStore();
  const restablecer = useRestablecerContraseniaRecuperacion();
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  const payload = useMemo(() => {
    return isValidState(location.state) ? location.state : null;
  }, [location.state]);

  const correo = payload?.correo.trim() ?? '';
  const claveRecuperacion = payload?.claveRecuperacion ?? '';

  const { values, errors, setValue, validateForm, isFormValid } = useForm<FormValues>(
    {
      nuevaContrasena: '',
      confirmarContrasena: '',
    },
    {
      nuevaContrasena: (value: string) => validationUtils.validatePassword(value),
      confirmarContrasena: (value: string) =>
        !value ? 'Confirma la nueva contraseña' : null,
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!payload) return;

    if (!validateForm()) return;

    if (values.nuevaContrasena !== values.confirmarContrasena) {
      showNotification('La nueva contraseña y la confirmación no coinciden.', 'error');
      return;
    }

    setConfirmationOpen(true);
  };

  const handleConfirm = useCallback(async () => {
    setConfirmationOpen(false);
    if (!payload) return;
    try {
      await restablecer.mutateAsync({
        Correo: correo,
        PSSActual: '',
        NuevaContrasenia: values.nuevaContrasena,
        ConfirmarContrasenia: values.confirmarContrasena,
        clave: claveRecuperacion,
      });
    } catch {
      // Errores notificados por el hook
    }
  }, [restablecer, payload, correo, claveRecuperacion, values]);

  const loading = restablecer.isPending;

  const passwordMismatch =
    values.nuevaContrasena.length > 0 &&
    values.confirmarContrasena.length > 0 &&
    values.nuevaContrasena !== values.confirmarContrasena;

  const mismatchMessage = 'Las contraseñas no coinciden.';

  const inputInteractiveClass =
    'rounded-lg border-slate-200 bg-white py-2.5 text-slate-900 shadow-sm transition-all duration-200 ' +
    'placeholder:text-slate-400 hover:border-slate-300 hover:shadow ' +
    'focus:border-host-500 focus:ring-2 focus:ring-host-500/25 focus:ring-offset-0';

  if (!payload) {
    return <Navigate to="/olvide-contrasena" replace />;
  }

  return (
    <div
      className="fixed inset-0 z-[10050] flex min-h-screen flex-col overflow-y-auto bg-background-primary lg:flex-row"
      role="dialog"
      aria-modal="true"
      aria-labelledby="restablecer-contrasena-titulo"
    >
      {loading && (
        <Loader
          message="Guardando nueva contraseña..."
          subtitle="Por favor espera unos segundos"
          className="!z-[10060]"
        />
      )}

      <aside className="hidden lg:flex lg:w-1/2 flex-shrink-0 items-center justify-center bg-gradient-to-br p-4">
        <div className="w-full">
          <img
            src="/LOGOSRFACIL_CONSLOGAN.svg"
            alt="Rfacil Suite"
            className="mx-auto h-auto w-full max-w-md object-contain"
          />
        </div>
      </aside>

      <div className="flex w-full flex-1 items-center justify-center p-5 lg:w-1/2">
        <div className="flex w-full max-w-md flex-col items-center">
          <div className="mb-1 flex w-full flex-col items-center">
            <div className="mb-3 flex h-20 w-full max-w-[200px] items-center justify-center sm:h-24 sm:max-w-[220px]">
              <img
                src="/ICONO IMAGOTIPO.png"
                alt=""
                className="h-full w-full object-contain object-center"
              />
            </div>
          </div>

          <h1
            id="restablecer-contrasena-titulo"
            className="mt-5 mb-2 text-center text-2xl font-bold tracking-tight text-[#1a365d]"
          >
            Restablecer contraseña
          </h1>
          <p className="mb-2 max-w-sm px-1 text-center text-sm leading-relaxed text-slate-500">
            Define una contraseña nueva para{' '}
            <span className="font-semibold text-slate-700 break-all">{correo}</span>. Debe coincidir con la
            confirmación.
          </p>

          <Card
            className="w-full !rounded-xl !border !border-slate-200 !bg-white !shadow-sm"
            padding="lg"
          >
            <form
              onSubmit={handleSubmit}
              className="text-left [&_label]:!font-semibold [&_label]:!text-blue-950"
            >
              <div className="mb-5">
                <Input
                  label="Nueva contraseña"
                  type="password"
                  value={values.nuevaContrasena}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('nuevaContrasena', e.target.value)
                  }
                  error={
                    errors.nuevaContrasena ?? (passwordMismatch ? mismatchMessage : undefined)
                  }
                  required
                  leftIcon={<FontAwesomeIcon icon={faLock} className="text-slate-400" />}
                  placeholder="Mín. 8 caracteres, mayúscula, minúscula, número y símbolo"
                  className={inputInteractiveClass}
                />
              </div>

              <div className="mb-6">
                <Input
                  label="Confirmación de nueva contraseña"
                  type="password"
                  value={values.confirmarContrasena}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('confirmarContrasena', e.target.value)
                  }
                  error={
                    errors.confirmarContrasena ?? (passwordMismatch ? mismatchMessage : undefined)
                  }
                  required
                  leftIcon={<FontAwesomeIcon icon={faLock} className="text-slate-400" />}
                  placeholder="Repite la nueva contraseña"
                  className={inputInteractiveClass}
                />
              </div>

              <p className="mb-5 text-xs leading-relaxed text-slate-500">
                La contraseña debe cumplir las reglas de seguridad del sistema. Si hay requisitos adicionales, el
                servidor te mostrará el detalle al guardar.
              </p>

              <Button
                type="submit"
                variant="ghost"
                fullWidth
                size="lg"
                disabled={!isFormValid()}
                className="rounded-lg !bg-slate-500 !text-white py-3 text-[15px] font-semibold shadow-md transition-all duration-200 hover:!bg-slate-600 hover:shadow-lg active:scale-[0.99] focus:!ring-2 focus:!ring-slate-400 focus:!ring-offset-2 disabled:!opacity-50 disabled:active:scale-100"
              >
                Guardar contraseña
              </Button>

              <p className="mt-4 text-center">
                <Link
                  to="/olvide-contrasena"
                  className="text-sm text-host-600 hover:underline font-medium inline-flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="text-xs" />
                  Volver al flujo de recuperación
                </Link>
              </p>
            </form>
          </Card>
        </div>
      </div>

      <ConfirmacionDialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={() => {
          void handleConfirm();
        }}
        zIndex={12000}
        title="Confirmar nueva contraseña"
        subtitle="Verifica los datos antes de continuar"
        message={
          <>
            ¿Deseas{' '}
            <span className="font-semibold text-slate-900">guardar esta contraseña</span>? Podrás iniciar sesión con
            ella enseguida.
          </>
        }
        confirmText="Confirmar"
        cancelText="Cancelar"
        loading={loading}
      />
    </div>
  );
};

export default RestablecerPassword;
